import { NSARouterMakerResult, NSABoxSecret } from "namirasoft-account-react";
import { DatabaseCreateRow, DatabaseRow, DatabaseType } from "namirasoft-credential";
import { NSBox, NSBoxBoolean, NSBoxEnum, NSBoxInteger, NSBoxString, NSBoxTextArea, NSColumn, NSRow } from "namirasoft-site-react";
import { Component, createRef } from "react";
import { CredentialEntities } from "../CredentialEntities";
import { CredentialLayout } from '../CredentialLayout';
import { CredentialProps } from "../CredentialProps";

interface DatabaseEditPageProps extends NSARouterMakerResult, CredentialProps
{
    isEdit: boolean;
}

interface DatabaseEditPageState
{
    ssl_enable: boolean;
}

export default class DatabaseEditPage extends Component<DatabaseEditPageProps, DatabaseEditPageState>
{
    NSBoxString_Name = createRef<NSBoxString>();
    NSBoxEnum_Type = createRef<NSBoxEnum<DatabaseType>>();
    NSBoxString_Host = createRef<NSBoxString>();
    NSBoxInteger_Port = createRef<NSBoxInteger>();
    NSBoxString_DatabaseName = createRef<NSBoxString>();
    NSABoxSecret_Username = createRef<NSABoxSecret>();
    NSABoxSecret_Password = createRef<NSABoxSecret>();
    NSABoxSecret_ConnectionString = createRef<NSABoxSecret>();
    NSBoxBoolean_SSLEnable = createRef<NSBoxBoolean>();
    NSABoxSecret_SSLCA = createRef<NSABoxSecret>();
    NSABoxSecret_SSLCert = createRef<NSABoxSecret>();
    NSABoxSecret_SSLKey = createRef<NSABoxSecret>();
    NSBoxTextArea_Description = createRef<NSBoxTextArea>();

    constructor(props: DatabaseEditPageProps)
    {
        super(props);
        this.state = { ssl_enable: false };
    }

    override render()
    {
        return (
            <CredentialLayout<DatabaseRow, DatabaseCreateRow>
                {...this.props}
                action={{
                    description: this.props.isEdit ? "This section allows you to edit a database credential by updating its name, host, username, password, or description. The username and password are kept as protected secrets, and any application set up to use the credential applies the updated values." : "This section allows you to create a Database credential for connecting to any database, such as MySQL, PostgreSQL, MongoDB, Redis, ClickHouse, or Elasticsearch, by entering a name, host, username, and password. Namirasoft encrypts the sensitive data for secure access, and any Namirasoft application you set up to use it can then connect to the database."
                }}
                pages={{
                    entity: CredentialEntities.database(this.props),
                    edit: {
                        ...this.props,
                        ui: {
                            tab_page_edit: {
                                editor: {
                                    getInputRow: (): DatabaseCreateRow =>
                                    {
                                        let name = this.NSBoxString_Name.current?.getValue() ?? "";
                                        let type = this.NSBoxEnum_Type.current?.getValueEnumOne(DatabaseType, DatabaseType.MySQL) ?? DatabaseType.MySQL;
                                        let host = this.NSBoxString_Host.current?.getValue() ?? null;
                                        let port = this.NSBoxInteger_Port.current?.getValue() ?? null;
                                        let database_name = this.NSBoxString_DatabaseName.current?.getValue() ?? null;
                                        let username = this.NSABoxSecret_Username.current?.getValue() ?? null;
                                        let password = this.NSABoxSecret_Password.current?.getValue() ?? null;
                                        let connection_string = this.NSABoxSecret_ConnectionString.current?.getValue() ?? null;
                                        let ssl_enable = this.NSBoxBoolean_SSLEnable.current?.getValue() ?? false;
                                        let ssl_ca = this.NSABoxSecret_SSLCA.current?.getValue() ?? null;
                                        let ssl_cert = this.NSABoxSecret_SSLCert.current?.getValue() ?? null;
                                        let ssl_key = this.NSABoxSecret_SSLKey.current?.getValue() ?? null;
                                        let description = this.NSBoxTextArea_Description.current?.getValue() ?? null;

                                        return {
                                            workspace_id: "",
                                            ssh_credential_id: null,
                                            name,
                                            type,
                                            host,
                                            port,
                                            database_name,
                                            username,
                                            password,
                                            connection_string,
                                            ssl_enable,
                                            ssl_ca,
                                            ssl_cert,
                                            ssl_key,
                                            description,
                                            database_category: [],
                                            database_field: [],
                                            database_tag: [],
                                        };
                                    },
                                    setRow: (row: DatabaseRow) =>
                                    {
                                        this.NSBoxString_Name.current?.setValue(row.name);
                                        this.NSBoxEnum_Type.current?.setValue(row.type);
                                        this.NSBoxString_Host.current?.setValue(row.host);
                                        this.NSBoxInteger_Port.current?.setValue(row.port);
                                        this.NSBoxString_DatabaseName.current?.setValue(row.database_name);
                                        this.NSABoxSecret_Username.current?.setValue(row.username_secret_id);
                                        this.NSABoxSecret_Password.current?.setValue(row.password_secret_id);
                                        this.NSABoxSecret_ConnectionString.current?.setValue(row.connection_string_secret_id);
                                        this.NSBoxBoolean_SSLEnable.current?.setValueBool(row.ssl_enable);
                                        this.setState(() => ({ ssl_enable: row.ssl_enable }), () =>
                                        {
                                            this.NSABoxSecret_SSLCA.current?.setValue(row.ssl_ca_secret_id);
                                            this.NSABoxSecret_SSLCert.current?.setValue(row.ssl_cert_secret_id);
                                            this.NSABoxSecret_SSLKey.current?.setValue(row.ssl_key_secret_id);
                                        });
                                        this.NSBoxTextArea_Description.current?.setValue(row.description);
                                    }
                                },
                                ui: {
                                    extra_tabs: [{
                                        title: "SSL",
                                        clicked: true,
                                        getContent: () => (
                                            <NSColumn style={{ width: NSBox.width.double }}>
                                                <NSBoxBoolean
                                                    ref={this.NSBoxBoolean_SSLEnable}
                                                    required={false}
                                                    title="SSL Enable"
                                                    onChanged={(box) => this.setState(() => ({ ssl_enable: box.getValue() ?? false }))}
                                                    style={{ width: NSBox.width.double }}
                                                />
                                                <NSRow style={this.state.ssl_enable ? {} : { display: "none" }}>
                                                    <NSABoxSecret
                                                        {...this.props}
                                                        ref={this.NSABoxSecret_SSLCA}
                                                        required={false}
                                                        title="SSL CA"
                                                        style={{ width: NSBox.width.double }}
                                                    />
                                                </NSRow>
                                                <NSRow style={this.state.ssl_enable ? {} : { display: "none" }}>
                                                    <NSABoxSecret
                                                        {...this.props}
                                                        ref={this.NSABoxSecret_SSLCert}
                                                        required={false}
                                                        title="SSL Cert"
                                                        style={{ width: NSBox.width.double }}
                                                    />
                                                </NSRow>
                                                <NSRow style={this.state.ssl_enable ? {} : { display: "none" }}>
                                                    <NSABoxSecret
                                                        {...this.props}
                                                        ref={this.NSABoxSecret_SSLKey}
                                                        required={false}
                                                        title="SSL Key"
                                                        style={{ width: NSBox.width.double }}
                                                    />
                                                </NSRow>
                                            </NSColumn>
                                        )
                                    }]
                                }
                            },
                        }
                    }
                }}
            >
                <NSColumn>
                    <NSRow>
                        <NSBoxString
                            ref={this.NSBoxString_Name}
                            required
                            title="Name"
                        />
                        <NSBoxEnum<DatabaseType>
                            ref={this.NSBoxEnum_Type}
                            title="Type"
                            required
                            multiple={false}
                            getEnumObject={() => DatabaseType}
                        />
                    </NSRow>
                    <NSRow>
                        <NSBoxString
                            ref={this.NSBoxString_Host}
                            required={false}
                            title="Host"
                        />
                        <NSBoxInteger
                            ref={this.NSBoxInteger_Port}
                            required={false}
                            title="Port"
                        />
                    </NSRow>
                    <NSBoxString
                        ref={this.NSBoxString_DatabaseName}
                        required={false}
                        title="Database Name"
                        style={{ width: NSBox.width.double }}
                    />
                    <NSRow>
                        <NSABoxSecret
                            {...this.props}
                            ref={this.NSABoxSecret_Username}
                            required={false}
                            title="Username"
                            auto_complete="new-password"
                        />
                        <NSABoxSecret
                            {...this.props}
                            ref={this.NSABoxSecret_Password}
                            required={false}
                            title="Password"
                            auto_complete="new-password"
                        />
                    </NSRow>
                    <NSABoxSecret
                        {...this.props}
                        ref={this.NSABoxSecret_ConnectionString}
                        required={false}
                        title="Connection String"
                        auto_complete="new-password"
                        style={{ width: NSBox.width.double }}
                    />
                    <NSBoxTextArea
                        ref={this.NSBoxTextArea_Description}
                        required={false}
                        title="Description"
                        style={{ width: NSBox.width.double }}
                    />
                </NSColumn>
            </CredentialLayout>
        );
    }
}